import MuiSelect, { SelectProps } from '@mui/material/Select';
import { ReactNode } from 'react';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { InputBaseProps } from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';

export type SelectSlotProps = {
  inputLabel?: Record<string, unknown>;
  input?: Record<string, unknown>;
  /** Props spread onto the combobox div (role="combobox"), e.g. data-testid */
  selectDisplay?: Record<string, unknown>;
  formHelperText?: Record<string, unknown>;
};

export type CustomSelectProps = SelectProps &
  InputBaseProps & {
    helperText?: ReactNode;
    className?: string;
    slotProps?: SelectSlotProps;
  };

const Select = ({
  id,
  label,
  error = false,
  fullWidth = false,
  disabled = false,
  helperText,
  variant = 'outlined',
  className,
  placeholder,
  required,
  value,
  slotProps,
  ...rest
}: CustomSelectProps) => {
  // Generate a default ID if none is provided
  const selectId = id ?? `select-${Math.random().toString(36).substring(2, 9)}`;

  // Performance Fix: Normalize null/undefined to empty string to prevent MUI warnings
  const normalizedValue = value === null || value === undefined ? '' : value;

  // Determine if label should shrink based on value presence
  const hasValue = normalizedValue !== '';

  const { inputProps: restInputProps, ...restWithoutInputProps } = rest as {
    inputProps?: Record<string, unknown>;
    'aria-describedby'?: string;
  };
  const mergedInputProps = { ...restInputProps, ...slotProps?.input };

  return (
    <FormControl fullWidth={fullWidth} error={error} disabled={disabled} variant={variant} className={className}>
      {label && (
        <InputLabel
          id={`${selectId}-label`}
          htmlFor={selectId}
          disabled={disabled}
          shrink={hasValue}
          {...slotProps?.inputLabel}
          // TODO: Remove the * from the label to the FormControl. Needs to retest how validation runs for forms with selected fields
          // https://rapidworksco.atlassian.net/browse/RAPTOR-5180
          sx={{
            '&::after': required
              ? {
                  content: '" *"',
                  color: 'error.main'
                }
              : {}
          }}
        >
          {label}
        </InputLabel>
      )}
      <MuiSelect
        labelId={label ? `${selectId}-label` : undefined}
        disabled={disabled}
        value={normalizedValue}
        label={label || undefined}
        aria-describedby={
          [restWithoutInputProps['aria-describedby'], helperText ? `${selectId}-helper-text` : undefined].filter(Boolean).join(' ') ||
          undefined
        }
        input={<OutlinedInput id={selectId} label={label || undefined} notched={hasValue} />}
        {...restWithoutInputProps}
        inputProps={mergedInputProps}
        SelectDisplayProps={slotProps?.selectDisplay}
      />
      {helperText && (
        <FormHelperText id={`${selectId}-helper-text`} {...slotProps?.formHelperText}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default Select;
